import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  MenuItem,
  TableContainer,
  Paper,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { apiRequest } from '../utils/apiClient';
import { OfferComponentOptions } from '../constants/Constants';

interface Offer {
  offerCode: string;
  title: string;
  discountPercent: number;
  applyToAll: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  usageCount?: number;
  applicableComponent: string;
}

const OffersPage: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [form, setForm] = useState<Omit<Offer, 'id' | 'usageCount'>>({
    title: '',
    offerCode: '',
    discountPercent: 0,
    applyToAll: false,
    startDate: null,
    endDate: null,
    applicableComponent: ''
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [offerCodeError, setOfferCodeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  useEffect( () => {

    
    
    const fetchOffers = async () => {
    try{
    const response = await apiRequest('/offers');
    if (response) {
        setOffers(response);
    }
    } catch (error) {   
        console.error('Error fetching offers:', error);
      setErrorMessage('Failed to fetch offers. Please try again.');
        }
    }

    fetchOffers();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
  
    const isCheckbox = (target: EventTarget): target is HTMLInputElement =>
      (target as HTMLInputElement).type === 'checkbox';

    console.log('handleChange', { name, value, type });
  
    setForm((prev) => ({
      ...prev,
      [name]: isCheckbox(e.target) ? e.target.checked : value,
    }));
  };
  

  const handleDateChange = (key: 'startDate' | 'endDate', value: Dayjs | null) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  

  const resetForm = () => {
    setForm({ title: '', offerCode:'', discountPercent: 0, applyToAll: false, startDate: null, endDate: null, applicableComponent: '' });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    console.log('handleSubmit', form);
    if (!form.title || !form.startDate || !form.endDate) return;

    setErrorMessage(null);

    const payload = {
      ...form,
      startDate: form.startDate?.toISOString(),
      endDate: form.endDate?.toISOString(),
    };

    try {
      setIsSubmitting(true);

      if (editingId) {
        await apiRequest(`/offers/${editingId}`,{ method: "PUT", body: payload } );
      } else {
        await apiRequest('/offers', { method: "POST", body: payload });
      }

      const refreshed = await apiRequest('/offers');
      setOffers(refreshed || []);
      resetForm();
    } catch (error: any) {
      const message =
        error?.body?.message ||
        (typeof error?.body === 'string' ? error.body : null) ||
        error?.message ||
        'Failed to save offer. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (offer: Offer) => {
    setForm({
      offerCode: offer.offerCode,
      title: offer.title,
      discountPercent: offer.discountPercent,
      applyToAll: offer.applyToAll,
      startDate: dayjs(offer.startDate),
      endDate: dayjs(offer.endDate),
      applicableComponent: offer.applicableComponent || ''
    });
    setEditingId(offer.offerCode);
  };

  const handleDelete = async (id: string) => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await apiRequest(`/offers/${id}`, { method: "DELETE" });
      const refreshed = await apiRequest('/offers');
      setOffers(refreshed || []);
    } catch (error: any) {
      const message =
        error?.body?.message ||
        (typeof error?.body === 'string' ? error.body : null) ||
        error?.message ||
        'Failed to delete offer. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

return (
  <>
    <Typography variant="h5" gutterBottom align="left" sx={{ fontWeight: 700 }}>
      Offers Admin Page
    </Typography>

    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 0, md: 2 } }}>
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2} alignItems="stretch">
            <TextField
              label="Offer Code"
              name="offerCode"
              value={form.offerCode}
              onChange={(e) => {
                handleChange(e);
                const isDuplicate =
                  Array.isArray(offers) &&
                  offers.some(
                    (offer) => offer.offerCode === e.target.value && offer.offerCode !== editingId
                  );
                setOfferCodeError(
                  isDuplicate ? 'Offer Code already exists. Please use a different code.' : null
                );
              }}
              error={!!offerCodeError}
              helperText={offerCodeError}
              size="small"
              fullWidth
            />

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2} width="100%">
              <TextField
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                size="small"
                fullWidth
              />
              <TextField
                label="Discount (%)"
                name="discountPercent"
                type="number"
                value={form.discountPercent}
                onChange={handleChange}
                size="small"
                fullWidth
              />
            </Box>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2} width="100%">
              <DatePicker
                label="Start Date"
                value={form.startDate}
                onChange={(newValue: Dayjs | null) => handleDateChange('startDate', newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
              <DatePicker
                label="End Date"
                value={form.endDate}
                onChange={(newValue: Dayjs | null) => handleDateChange('endDate', newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </Box>

          <TextField
            select
            label="Applicable Component"
            name="applicableComponent"
            value={form.applicableComponent}
            onChange={handleChange}
            size="small"
            fullWidth
          >
            {OfferComponentOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>


            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }} gap={2} width="100%">
              <FormControlLabel
                control={
                  <Checkbox
                    name="applyToAll"
                    checked={form.applyToAll}
                    onChange={handleChange}
                    size="small"
                  />
                }
                label="Apply to All Products"
              />
              <Button variant="contained" size="small" onClick={handleSubmit} disabled={isSubmitting || !!offerCodeError}>
                {isSubmitting ? 'Saving...' : editingId ? 'Update Offer' : 'Create Offer'}
              </Button>
            </Box>
            {errorMessage && (
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {offers && offers.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom align="left" sx={{ fontWeight: 700 }}>
            Existing Offers
          </Typography>

          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Offer Code</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Discount %</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Applicable Component</TableCell>
                <TableCell>Apply To All</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((offer) => (
                <TableRow key={offer.offerCode}>
                  <TableCell>{offer.offerCode}</TableCell>
                  <TableCell>{offer.title}</TableCell>
                  <TableCell>{offer.discountPercent}%</TableCell>
                  <TableCell>
                    {dayjs(offer.startDate).format('YYYY-MM-DD')} -{' '}
                    {dayjs(offer.endDate).format('YYYY-MM-DD')}
                  </TableCell>
                  <TableCell>{offer.applicableComponent || ''}</TableCell>
                  <TableCell>{offer.applyToAll ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{offer.usageCount ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleEdit(offer)} disabled={isSubmitting}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(offer.offerCode)} disabled={isSubmitting}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      )}
    </Container>
  </>
);

};

export default OffersPage;
